import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { SlidersHorizontal } from 'lucide-react';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useAuth } from '@/contexts/AuthContext';
import {
  demandPlanningSettingsService,
  type ForecastConfigRow,
} from '@/services/demandPlanning/demandPlanningSettings.service';

import { HelpTip, LabelWithHelp } from './HelpTip';

type ConfigDraft = Pick<
  ForecastConfigRow,
  | 'horizon_periods'
  | 'bucket_granularity'
  | 'accuracy_metric'
  | 'outlier_policy'
  | 'seasonality_enabled'
  | 'new_product_policy'
  | 'intermittent_policy'
  | 'algorithm_code'
>;

function pickDraft(row: ForecastConfigRow): ConfigDraft {
  return {
    horizon_periods: row.horizon_periods,
    bucket_granularity: row.bucket_granularity,
    accuracy_metric: row.accuracy_metric,
    outlier_policy: row.outlier_policy,
    seasonality_enabled: row.seasonality_enabled,
    new_product_policy: row.new_product_policy,
    intermittent_policy: row.intermittent_policy,
    algorithm_code: row.algorithm_code,
  };
}

export function ForecastConfigSettingsCard() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const canEdit = user?.role?.id === 1;

  const configs = useQuery({
    queryKey: ['forecast-configs-list'],
    queryFn: () => demandPlanningSettingsService.listForecastConfigs(),
  });

  const activeConfig =
    configs.data?.find((c) => c.scope_type === 'global' && c.status === 'active') ??
    configs.data?.[0] ??
    null;

  const [draft, setDraft] = useState<ConfigDraft | null>(null);
  const [saveState, setSaveState] = useState<'success' | 'error' | null>(null);

  useEffect(() => {
    if (activeConfig) setDraft(pickDraft(activeConfig));
  }, [activeConfig?.id]);

  const save = useMutation({
    mutationFn: () =>
      demandPlanningSettingsService.patchForecastConfig(activeConfig!.id, draft!),
    onSuccess: () => {
      setSaveState('success');
      void queryClient.invalidateQueries({ queryKey: ['forecast-configs-list'] });
      void queryClient.invalidateQueries({ queryKey: ['forecast-run-insights'] });
    },
    onError: () => setSaveState('error'),
  });

  if (!configs.isLoading && !activeConfig) {
    return (
      <Card className="mt-6">
        <CardHeader>
          <CardTitle className="text-lg">{t('settings.forecastConfig.title')}</CardTitle>
          <CardDescription>{t('settings.forecastConfig.noConfig')}</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <Card className="mt-6">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <SlidersHorizontal className="w-5 h-5 text-stocker-primary" />
          {t('settings.forecastConfig.title')}
          <HelpTip>{t('settings.forecastConfig.help')}</HelpTip>
        </CardTitle>
        <CardDescription>
          {t('settings.forecastConfig.description', { id: activeConfig?.id ?? '—' })}
        </CardDescription>
      </CardHeader>
      <CardContent>
        {configs.isLoading || !draft ? (
          <p className="text-sm text-gray-500">{t('common.loading')}</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <label className="flex flex-col gap-1 text-sm">
              <LabelWithHelp
                label={t('settings.forecastConfig.horizon')}
                help={t('settings.forecastConfig.horizonHelp')}
                className="text-gray-700"
              />
              <input
                type="number"
                min={4}
                max={104}
                className="rounded-md border border-gray-300 px-2 py-1.5"
                disabled={!canEdit}
                value={draft.horizon_periods}
                onChange={(e) =>
                  setDraft((d) =>
                    d ? { ...d, horizon_periods: Number(e.target.value) } : d,
                  )
                }
              />
            </label>
            <label className="flex flex-col gap-1 text-sm">
              <LabelWithHelp
                label={t('settings.forecastConfig.granularity')}
                help={t('settings.forecastConfig.granularityHelp')}
                className="text-gray-700"
              />
              <select
                className="rounded-md border border-gray-300 px-2 py-1.5"
                disabled={!canEdit}
                value={draft.bucket_granularity}
                onChange={(e) =>
                  setDraft((d) => (d ? { ...d, bucket_granularity: e.target.value } : d))
                }
              >
                <option value="week">{t('consensus.granularityWeek')}</option>
                <option value="month">{t('consensus.granularityMonth')}</option>
              </select>
            </label>
            <label className="flex flex-col gap-1 text-sm">
              <LabelWithHelp
                label={t('settings.forecastConfig.accuracyMetric')}
                help={t('settings.forecastConfig.accuracyMetricHelp')}
                className="text-gray-700"
              />
              <select
                className="rounded-md border border-gray-300 px-2 py-1.5"
                disabled={!canEdit}
                value={draft.accuracy_metric}
                onChange={(e) =>
                  setDraft((d) => (d ? { ...d, accuracy_metric: e.target.value } : d))
                }
              >
                <option value="mape">MAPE</option>
                <option value="wape">WAPE</option>
                <option value="smape">sMAPE</option>
                <option value="rmse">RMSE</option>
              </select>
            </label>
            <label className="flex flex-col gap-1 text-sm">
              <LabelWithHelp
                label={t('settings.forecastConfig.outlierPolicy')}
                help={t('settings.forecastConfig.outlierPolicyHelp')}
                className="text-gray-700"
              />
              <select
                className="rounded-md border border-gray-300 px-2 py-1.5"
                disabled={!canEdit}
                value={draft.outlier_policy}
                onChange={(e) =>
                  setDraft((d) => (d ? { ...d, outlier_policy: e.target.value } : d))
                }
              >
                <option value="cap">{t('settings.forecastConfig.outlierCap')}</option>
                <option value="ignore">{t('settings.forecastConfig.outlierIgnore')}</option>
                <option value="replace">{t('settings.forecastConfig.outlierReplace')}</option>
                <option value="keep">{t('settings.forecastConfig.outlierKeep')}</option>
              </select>
            </label>
            <label className="flex items-center gap-2 text-sm sm:col-span-2">
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-gray-300 text-stocker-primary"
                disabled={!canEdit}
                checked={draft.seasonality_enabled}
                onChange={(e) =>
                  setDraft((d) => (d ? { ...d, seasonality_enabled: e.target.checked } : d))
                }
              />
              <LabelWithHelp
                label={t('settings.forecastConfig.seasonality')}
                help={t('settings.forecastConfig.seasonalityHelp')}
              />
            </label>
          </div>
        )}
        {canEdit && draft ? (
          <div className="mt-4 flex items-center gap-3">
            <button
              type="button"
              disabled={save.isPending}
              className="px-4 py-2 rounded-md text-sm font-medium text-white bg-stocker-primary hover:opacity-90 disabled:opacity-50"
              onClick={() => {
                setSaveState(null);
                save.mutate();
              }}
            >
              {save.isPending ? t('common.loading') : t('common.save')}
            </button>
            {saveState === 'success' ? (
              <span className="text-sm text-green-600">{t('settings.saveSuccess')}</span>
            ) : null}
            {saveState === 'error' ? (
              <span className="text-sm text-red-600">{t('settings.saveError')}</span>
            ) : null}
          </div>
        ) : null}
        {!canEdit ? (
          <p className="mt-2 text-xs text-gray-500">{t('settings.forecastMotor.readOnly')}</p>
        ) : null}
      </CardContent>
    </Card>
  );
}
